import React from 'react';
import { TaskItem } from '../types/dashboardTypes';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DashboardTaskCard from '../DashboardTaskCard';
import { Types } from './taskTypes';

interface DraggableDashboardTaskCardProps {
  task: TaskItem;
  sectionType: 'overdue' | 'today' | 'thisWeek' | 'highlighted';
  projects: Array<{ id: string; name: string }>;
  index?: number;
  onEditTask: (task: TaskItem) => void;
  onUpdateTask: (taskId: string, updates: Partial<TaskItem>) => Promise<void>;
  onDeleteTask: (taskId: string) => Promise<void>;
  onNavigateToTask: (task: TaskItem) => void;
  disabled?: boolean;
}

/**
 * Draggable wrapper for dashboard task cards using @dnd-kit/sortable
 * Makes task cards draggable while preserving their visual appearance
 */
const DraggableDashboardTaskCard: React.FC<DraggableDashboardTaskCardProps> = ({
  task,
  sectionType,
  projects,
  index = 0,
  onEditTask,
  onUpdateTask,
  onDeleteTask,
  onNavigateToTask,
  disabled = false
}) => {
  // Set up DND Kit functionality - similar to task topic but without sorting logic
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: {
      task,
      index,
      type: Types.TASK,
      // No need for sorting-specific data as we only drag between columns
    },
    disabled
  });

  // Styles for the draggable item - only transform, no visual changes
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      className="relative touch-manipulation" 
      data-dragging={isDragging ? 'true' : 'false'}
    >
      <DashboardTaskCard
        task={task}
        sectionType={sectionType}
        projects={projects}
        onEditTask={onEditTask}
        onUpdateTask={onUpdateTask}
        onDeleteTask={onDeleteTask}
        onNavigateToTask={onNavigateToTask}
        customDragHandlers={listeners}
      />
    </div>
  );
};

export default DraggableDashboardTaskCard;